import React, { useState, useEffect } from 'react';
import { Button, Modal, List, message, Spin } from 'antd';
import { SwapOutlined, CheckOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Dispatch } from '@reduxjs/toolkit';
import classNames from 'classnames';

import { AccessibleAccount, AccessibleAccountsResponse } from '../../../../types/account';
import { ApplicationState } from '../../../reducers';
import {
  fetchAccessibleAccounts,
  switchAccount,
  switchBackToOriginalAccount,
} from '../../../actions/user';
import { hasPermission } from '../../../utils/Permissions';

type ChangeAccountButtonProps = {
  dispatch: Dispatch;
  accessibleAccounts?: AccessibleAccountsResponse;
  loading?: boolean;
  user: any;
};

const ChangeAccountButton: React.FC<ChangeAccountButtonProps> = ({
  dispatch,
  accessibleAccounts,
  loading = false,
  user,
}) => {
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);
  const [switchingAccount, setSwitchingAccount] = useState(false);
  const isMountedRef = React.useRef(true);

  useEffect(() => {
    if (modalVisible) {
      // Always fetch fresh accessible accounts when modal opens
      dispatch(fetchAccessibleAccounts());
    }
  }, [modalVisible, dispatch]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const executeAccountAction = async (action: any, successMessage: string) => {
    setSwitchingAccount(true);
    try {
      await dispatch(action);
      message.success(successMessage);
      // Complete page reload like login to ensure clean state
      window.location.reload();
    } catch (error: any) {
      message.error(error?.response?.data?.message || t('validation:unknownApiError'));
      if (isMountedRef.current) {
        setSwitchingAccount(false);
      }
    }
  };

  const handleAccountSwitch = (targetAccountId: number) => {
    executeAccountAction(
      switchAccount(targetAccountId, t('accountSettings:switchReason')),
      t('accountSettings:messages.account_switched')
    );
  };

  const handleSwitchBack = () => {
    executeAccountAction(
      switchBackToOriginalAccount(),
      t('accountSettings:messages.switched_back')
    );
  };

  const renderAccountItem = (account: AccessibleAccount) => {
    const isCurrentAccount = account.is_current;

    return (
      <List.Item
        className={classNames('account-list-item', {
          'account-list-item--current': isCurrentAccount,
        })}
        onClick={() => !isCurrentAccount && handleAccountSwitch(account.id)}
        actions={[
          isCurrentAccount ? (
            <CheckOutlined className="account-check-icon" />
          ) : (
            <Button
              type="link"
              size="small"
              disabled={switchingAccount}
              onClick={e => {
                e.stopPropagation();
                handleAccountSwitch(account.id);
              }}
            >
              {t('common:switch')}
            </Button>
          ),
        ]}
      >
        <List.Item.Meta
          title={account.name}
          description={`ID: ${account.id} ${account.is_admin ? '• Admin' : ''}`}
        />
      </List.Item>
    );
  };

  const accounts = accessibleAccounts?.accessible_accounts || [];
  const hasActiveSwitch = accessibleAccounts?.has_active_switch || false;
  const canAccessAccountSwitch = hasPermission(user, 'can_access_account_switch');

  const cancelButton = (
    <Button key="cancel" onClick={() => setModalVisible(false)}>
      {t('common:cancel')}
    </Button>
  );

  if (!canAccessAccountSwitch) {
    return null;
  }

  return (
    <>
      <Button icon={<SwapOutlined />} onClick={() => setModalVisible(true)} loading={loading}>
        {t('accountSettings:changeAccount')}
      </Button>

      <Modal
        title={t('accountSettings:selectAccount')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={
          hasActiveSwitch
            ? [
                <Button key="switch-back" onClick={handleSwitchBack} loading={switchingAccount}>
                  {t('accountSettings:switchBackToOriginal')}
                </Button>,
                cancelButton,
              ]
            : [cancelButton]
        }
        width={600}
        className="change-account-modal"
      >
        {loading ? (
          <div className="loading-container">
            <Spin size="large" />
            <div className="loading-text">{t('accountSettings:loadingAccounts')}</div>
          </div>
        ) : (
          <List
            dataSource={accounts}
            renderItem={renderAccountItem}
            locale={{ emptyText: t('accountSettings:noAccessibleAccounts') }}
          />
        )}
      </Modal>
    </>
  );
};

const mapStateToProps = (state: ApplicationState) => ({
  accessibleAccounts: state.user.accessibleAccounts,
  loading: state.user.loadingAccessibleAccounts,
  user: state.user.user,
});

export default connect(mapStateToProps)(ChangeAccountButton);
